import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  FaArrowLeft,
  FaGlobe,
  FaCheckCircle,
  FaHandshake,
  FaDollarSign,
  FaCheck,
  FaHome,
  FaBuilding,
  FaPray,
  FaCar,
  FaLink,
  FaShieldAlt,
  FaDumbbell,
  FaAward,
  FaBriefcase,
  FaTruck,
  FaChartLine
} from 'react-icons/fa';

/* ============================================================
   LOCAL IMAGE IMPORTS
   ============================================================ */

import rhLogo from '../logos/RHEnterprises/rhenterpriseslogo.png';

import cargoImage from '../logos/RHEnterprises/rhenterprises1.png';
import warehouseImage from '../logos/RHEnterprises/rhenterprises2.png';

import residentialImage from '../logos/RHEnterprises/rhenterprises3.png';
import commercialImage from '../logos/RHEnterprises/rhenterprises4.png';
import mosqueImage from '../logos/RHEnterprises/rhenterprises5.png';
import automotiveImage from '../logos/RHEnterprises/rhenterprises6.png';

import carpetRollsImage from '../logos/RHEnterprises/rhenterprises2.png';
import backingImage from '../logos/RHEnterprises/rhenterprises6.png';


export default function RHEnterprises() {

  const navigate = useNavigate();

  /* ============================================================
     SCROLL REVEAL ANIMATION
     ============================================================ */

  useEffect(() => {

    const revealElements =
      document.querySelectorAll('.rh-reveal');

    const observer =
      new IntersectionObserver(
        (entries) => {

          entries.forEach((entry) => {

            if (entry.isIntersecting) {

              entry.target.classList.add(
                'rh-visible'
              );

            }

          });

        },
        {
          threshold: 0.12
        }
      );

    revealElements.forEach((element) =>
      observer.observe(element)
    );

    return () => observer.disconnect();

  }, []);


  return (
    <>

      {/* ========================================================
          RESPONSIVE + ANIMATION CSS
          ======================================================== */}

      <style>{`

        * {
          box-sizing: border-box;
        }

        html {
          scroll-behavior: smooth;
        }

        body {
          margin: 0;
          padding: 0;
          overflow-x: hidden;
        }

        /* ======================================================
           MAIN PAGE
           ====================================================== */

        .rh-page {

          width: 100%;
          min-height: 100vh;

          background:
            radial-gradient(
              circle at top right,
              rgba(201, 145, 35, 0.12),
              transparent 30%
            ),
            #071426;

          font-family:
            Arial,
            Helvetica,
            sans-serif;

          color: #172033;

          overflow: hidden;
        }


        /* ======================================================
           TOP NAV
           ====================================================== */

        .rh-top-nav {

          width: 100%;

          padding: 14px 4%;

          background: #071426;

          display: flex;

          align-items: center;

          justify-content: space-between;

          position: relative;

          z-index: 20;
        }


        .rh-nav-brand {

          color: #ffffff;

          font-size: 16px;

          font-weight: 800;

          letter-spacing: 1.5px;
        }


        .rh-back-button {

          display: inline-flex;

          align-items: center;

          justify-content: center;

          gap: 8px;

          padding: 10px 18px;

          border: 1px solid #c99832;

          border-radius: 5px;

          background: transparent;

          color: #ffffff;

          font-size: 13px;

          font-weight: 700;

          cursor: pointer;

          transition:
            transform 0.3s ease,
            background 0.3s ease,
            box-shadow 0.3s ease;
        }


        .rh-back-button:hover {

          background: #c99832;

          transform: translateX(-4px);

          box-shadow:
            0 8px 20px
            rgba(201, 152, 50, 0.25);
        }


        /* ======================================================
           MAIN CONTAINER
           ====================================================== */

        .rh-main {

          width: 100%;

          background: #ffffff;

          overflow: hidden;
        }


        /* ======================================================
           HERO
           ====================================================== */

        .rh-hero {

          width: 100%;

          min-height: 620px;

          display: grid;

          grid-template-columns: 48% 52%;

          position: relative;

          overflow: hidden;

          background: #f8f7f3;
        }


        .rh-hero-left {

          position: relative;

          padding:
            clamp(45px, 7vw, 100px)
            clamp(30px, 7vw, 110px);

          display: flex;

          flex-direction: column;

          justify-content: center;

          z-index: 2;

          background: #f7f6f2;
        }


        .rh-hero-left::after {

          content: "";

          position: absolute;

          top: 0;

          right: -90px;

          width: 180px;

          height: 100%;

          background: #f7f6f2;

          clip-path:
            polygon(
              50% 0,
              100% 0,
              50% 100%,
              0 100%
            );

          z-index: -1;
        }


        .rh-logo {

          width: min(390px, 85%);

          height: auto;

          object-fit: contain;

          margin-bottom: 30px;

          animation:
            rhLogoFloat 5s
            ease-in-out infinite;
        }


        @keyframes rhLogoFloat {

          0%,
          100% {
            transform: translateY(0);
          }

          50% {
            transform: translateY(-7px);
          }

        }


        .rh-eyebrow {

          display: inline-flex;

          width: fit-content;

          padding: 8px 15px;

          margin-bottom: 18px;

          border-radius: 4px;

          background: #0b2948;

          color: #ffffff;

          font-size: 12px;

          font-weight: 800;

          letter-spacing: 1.2px;

          animation:
            rhFadeUp
            0.8s
            ease
            both;
        }


        .rh-main-title {

          margin: 0;

          color: #092746;

          font-family:
            Georgia,
            "Times New Roman",
            serif;

          font-size:
            clamp(38px, 4.4vw, 66px);

          line-height: 0.98;

          font-weight: 900;

          letter-spacing: -1.5px;

          animation:
            rhFadeUp
            0.9s
            0.1s
            ease
            both;
        }


        .rh-main-title span {

          color: #c99832;

          display: block;
        }


        .rh-hero-description {

          max-width: 650px;

          margin: 24px 0 0;

          color: #405064;

          font-size:
            clamp(14px, 1.1vw, 17px);

          line-height: 1.75;

          animation:
            rhFadeUp
            1s
            0.2s
            ease
            both;
        }


        .rh-hero-description-secondary {

          max-width: 620px;

          margin: 12px 0 0;

          color: #526174;

          font-size: 14px;

          line-height: 1.65;

          animation:
            rhFadeUp
            1s
            0.3s
            ease
            both;
        }


        @keyframes rhFadeUp {

          from {

            opacity: 0;

            transform:
              translateY(25px);

          }

          to {

            opacity: 1;

            transform:
              translateY(0);

          }

        }


        /* ======================================================
           HERO RIGHT
           ====================================================== */

        .rh-hero-right {

          position: relative;

          min-height: 620px;

          overflow: hidden;

          background: #0b2948;
        }


        .rh-cargo-image {

          width: 100%;

          height: 100%;

          object-fit: cover;

          display: block;

          transform: scale(1.03);

          animation:
            rhHeroZoom
            12s
            ease-in-out
            infinite
            alternate;
        }


        @keyframes rhHeroZoom {

          from {
            transform: scale(1.02);
          }

          to {
            transform: scale(1.1);
          }

        }


        .rh-hero-right::after {

          content: "";

          position: absolute;

          inset: 0;

          background:
            linear-gradient(
              90deg,
              rgba(7, 20, 38, 0.15),
              rgba(7, 20, 38, 0.05)
            );

          pointer-events: none;
        }


        /* ======================================================
           IMPORT BOX
           ====================================================== */

        .rh-import-box {

          position: absolute;

          left: 8%;

          right: 8%;

          bottom: 8%;

          z-index: 4;

          padding: 25px;

          border-radius: 12px;

          background:
            rgba(6, 27, 49, 0.94);

          border:
            1px solid
            rgba(201, 152, 50, 0.8);

          box-shadow:
            0 25px 60px
            rgba(0, 0, 0, 0.35);

          backdrop-filter: blur(7px);

          animation:
            rhImportFloat
            5s
            ease-in-out
            infinite;
        }


        @keyframes rhImportFloat {

          0%,
          100% {
            transform: translateY(0);
          }

          50% {
            transform: translateY(-8px);
          }

        }


        .rh-import-title {

          margin: 0 0 20px;

          color: #ffffff;

          font-size:
            clamp(18px, 2vw, 27px);

          font-weight: 900;

          line-height: 1.2;

          text-align: center;
        }


        .rh-import-title span {

          color: #c99832;
        }


        .rh-import-grid {

          display: grid;

          grid-template-columns:
            repeat(4, 1fr);

          gap: 12px;
        }


        .rh-import-item {

          min-height: 90px;

          padding: 10px 5px;

          display: flex;

          flex-direction: column;

          align-items: center;

          justify-content: center;

          text-align: center;

          border-right:
            1px solid
            rgba(255,255,255,0.2);

          transition:
            transform 0.3s ease,
            background 0.3s ease;
        }


        .rh-import-item:last-child {

          border-right: none;
        }


        .rh-import-item:hover {

          transform:
            translateY(-6px);

          background:
            rgba(201,152,50,0.1);
        }


        .rh-import-icon {

          font-size: 27px;

          color: #c99832;

          margin-bottom: 10px;
        }


        .rh-import-item span {

          color: #ffffff;

          font-size: 10px;

          line-height: 1.4;

          font-weight: 700;

          letter-spacing: 0.5px;
        }


        /* ======================================================
           SECTION
           ====================================================== */

        .rh-section {

          width: 100%;

          padding:
            clamp(50px, 7vw, 100px)
            clamp(20px, 6vw, 100px);
        }


        .rh-section-dark {

          background: #092746;

          color: #ffffff;
        }


        .rh-section-light {

          background: #f8f7f3;
        }


        .rh-section-header {

          max-width: 1200px;

          margin:
            0 auto 40px;

          text-align: center;
        }


        .rh-section-kicker {

          color: #c99832;

          font-size: 13px;

          font-weight: 900;

          letter-spacing: 2px;

          margin-bottom: 10px;
        }


        .rh-section-title {

          margin: 0;

          color: #092746;

          font-family:
            Georgia,
            "Times New Roman",
            serif;

          font-size:
            clamp(30px, 4vw, 52px);

          line-height: 1.05;
        }


        .rh-section-dark
        .rh-section-title {

          color: #ffffff;
        }


        .rh-section-line {

          width: 80px;

          height: 3px;

          margin:
            18px auto 0;

          background: #c99832;
        }


        /* ======================================================
           INFORMATION GRID
           ====================================================== */

        .rh-info-grid {

          width: 100%;

          max-width: 1400px;

          margin: 0 auto;

          display: grid;

          grid-template-columns:
            1fr 1fr;

          gap: 35px;
        }


        .rh-info-card {

          padding:
            clamp(25px, 3vw, 45px);

          border-radius: 12px;

          background: #ffffff;

          border:
            1px solid
            #ead8a9;

          box-shadow:
            0 15px 40px
            rgba(9,39,70,0.08);

          transition:
            transform 0.4s ease,
            box-shadow 0.4s ease;
        }


        .rh-info-card:hover {

          transform:
            translateY(-8px);

          box-shadow:
            0 25px 55px
            rgba(9,39,70,0.15);
        }


        .rh-card-title {

          margin:
            0 0 25px;

          color: #092746;

          font-size:
            clamp(17px, 1.5vw, 23px);

          font-weight: 900;

          letter-spacing: 1px;

          border-bottom:
            2px solid
            #c99832;

          padding-bottom: 14px;
        }


        .rh-product-list,
        .rh-why-list {

          display: flex;

          flex-direction: column;

          gap: 12px;
        }


        .rh-product-item,
        .rh-why-item {

          display: flex;

          align-items: center;

          gap: 13px;

          padding: 13px 15px;

          border-radius: 7px;

          background: #faf8f1;

          border:
            1px solid
            #eee2c5;

          transition:
            transform 0.3s ease,
            background 0.3s ease,
            border-color 0.3s ease;
        }


        .rh-product-item:hover,
        .rh-why-item:hover {

          transform:
            translateX(8px);

          background: #fffdf6;

          border-color: #c99832;
        }


        .rh-check-box {

          width: 25px;

          height: 25px;

          min-width: 25px;

          display: flex;

          align-items: center;

          justify-content: center;

          border-radius: 50%;

          background: #092746;

          color: #ffffff;

          font-size: 11px;
        }


        .rh-product-text,
        .rh-why-text {

          font-size: 14px;

          font-weight: 700;

          color: #26364a;
        }


        .rh-why-icon {

          min-width: 24px;

          color: #c99832;

          font-size: 20px;
        }


        .rh-card-image {

          width: 100%;

          height: 260px;

          margin-top: 25px;

          border-radius: 8px;

          overflow: hidden;
        }


        .rh-card-image img {

          width: 100%;

          height: 100%;

          object-fit: cover;

          display: block;

          transition:
            transform 0.7s ease;
        }


        .rh-card-image:hover img {

          transform:
            scale(1.07);
        }


        /* ======================================================
           APPLICATION AREAS
           ====================================================== */

        .rh-app-section {

          padding:
            clamp(55px, 7vw, 100px)
            clamp(20px, 5vw, 80px);

          background: #ffffff;
        }


        .rh-app-grid {

          width: 100%;

          display: grid;

          grid-template-columns:
            repeat(4, 1fr);

          gap: 18px;

          max-width: 1600px;

          margin: 0 auto;
        }


        .rh-app-card {

          position: relative;

          height: 310px;

          overflow: hidden;

          border-radius: 10px;

          cursor: pointer;

          box-shadow:
            0 15px 35px
            rgba(9,39,70,0.14);
        }


        .rh-app-card img {

          width: 100%;

          height: 100%;

          object-fit: cover;

          transition:
            transform 0.8s
            cubic-bezier(.2,.8,.2,1);
        }


        .rh-app-card:hover img {

          transform:
            scale(1.1);
        }


        .rh-app-card::after {

          content: "";

          position: absolute;

          inset: 0;

          background:
            linear-gradient(
              to top,
              rgba(4,20,38,0.9),
              rgba(4,20,38,0.05) 65%
            );
        }


        .rh-app-overlay {

          position: absolute;

          left: 0;

          right: 0;

          bottom: 0;

          z-index: 2;

          padding: 22px;

          display: flex;

          align-items: center;

          gap: 10px;

          color: #ffffff;

          font-size: 15px;

          font-weight: 900;

          letter-spacing: 0.5px;

          transition:
            transform 0.35s ease;
        }


        .rh-app-card:hover
        .rh-app-overlay {

          transform:
            translateY(-7px);
        }


        .rh-app-icon {

          width: 42px;

          height: 42px;

          min-width: 42px;

          display: flex;

          align-items: center;

          justify-content: center;

          border-radius: 50%;

          border:
            1px solid
            #c99832;

          color: #c99832;

          background:
            rgba(4,20,38,0.75);
        }


        /* ======================================================
           STRENGTH + ADVANTAGES
           ====================================================== */

        .rh-strength-grid {

          width: 100%;

          max-width: 1500px;

          margin: 0 auto;

          display: grid;

          grid-template-columns:
            minmax(0, 1fr)
            minmax(0, 1.25fr);

          gap: 35px;

          align-items: stretch;
        }


        /* ======================================================
           FIXED STRENGTH CARD
           ====================================================== */

        .rh-strength-card {

          position: relative;

          min-height: 500px;

          padding:
            clamp(30px, 4vw, 55px);

          border-radius: 14px;

          overflow: hidden;

          background: #071d35;

          border:
            1px solid
            rgba(201,152,50,0.5);

          display: flex;

          flex-direction: column;

          justify-content: flex-start;

          isolation: isolate;
        }


        /*
          Decorative background glow
        */

        .rh-strength-card::before {

          content: "";

          position: absolute;

          width: 260px;

          height: 260px;

          top: -100px;

          right: -80px;

          border-radius: 50%;

          background:
            rgba(201,152,50,0.08);

          pointer-events: none;

          z-index: -1;
        }


        .rh-strength-card h3 {

          margin: 0 0 18px;

          color: #c99832;

          font-size: 14px;

          letter-spacing: 2px;

          font-weight: 900;

          position: relative;

          z-index: 3;
        }


        .rh-strength-card h2 {

          margin: 0;

          max-width: 100%;

          color: #ffffff;

          font-family:
            Georgia,
            "Times New Roman",
            serif;

          font-size:
            clamp(30px, 3.2vw, 46px);

          line-height: 1.08;

          font-weight: 700;

          position: relative;

          z-index: 3;
        }


        .rh-strength-card p {

          max-width: 100%;

          margin: 23px 0 0;

          color: #d8e0ea;

          font-size: 15px;

          line-height: 1.8;

          position: relative;

          z-index: 3;

          padding-bottom: 225px;
        }


        /*
          Strength image fixed properly.

          Desktop:
          image sits at bottom-right.

          Text gets enough bottom padding
          so it never goes behind image.
        */

        .rh-strength-image {

          position: absolute;

          width: 58%;

          height: 210px;

          right: 0;

          bottom: 0;

          overflow: hidden;

          border-radius:
            12px
            0
            0
            0;

          z-index: 2;

          background: #0b2948;
        }


        .rh-strength-image img {

          width: 100%;

          height: 100%;

          object-fit: cover;

          object-position: center;

          display: block;

          transition:
            transform 0.7s ease;
        }


        .rh-strength-card:hover
        .rh-strength-image img {

          transform:
            scale(1.08);
        }


        /* ======================================================
           ADVANTAGES
           ====================================================== */

        .rh-advantages {

          padding:
            clamp(30px, 4vw, 50px);

          background: #ffffff;

          border-radius: 14px;

          border:
            1px solid
            #ead8a9;

          color: #172033;

          min-height: 500px;
        }


        .rh-advantages h3 {

          margin:
            0 0 25px;

          color: #092746;

          font-size: 16px;

          letter-spacing: 2px;
        }


        .rh-advantages-grid {

          display: grid;

          grid-template-columns:
            repeat(2, 1fr);

          gap: 15px;
        }


        .rh-advantage {

          min-height: 150px;

          padding: 20px;

          display: flex;

          flex-direction: column;

          align-items: center;

          justify-content: center;

          text-align: center;

          border-radius: 9px;

          background: #faf8f1;

          border:
            1px solid
            #eee2c5;

          transition:
            transform 0.35s ease,
            box-shadow 0.35s ease;
        }


        .rh-advantage:hover {

          transform:
            translateY(-7px);

          box-shadow:
            0 15px 30px
            rgba(9,39,70,0.12);
        }


        .rh-advantage-icon {

          width: 55px;

          height: 55px;

          display: flex;

          align-items: center;

          justify-content: center;

          margin-bottom: 13px;

          border-radius: 50%;

          background: #092746;

          color: #c99832;

          font-size: 23px;
        }


        .rh-advantage span {

          color: #26364a;

          font-size: 12px;

          font-weight: 900;

          line-height: 1.4;
        }


        .rh-global-callout {

          display: flex;

          align-items: center;

          gap: 18px;

          margin-top: 20px;

          padding: 18px;

          border-radius: 8px;

          background: #092746;

          color: #ffffff;
        }


        .rh-global-callout svg {

          min-width: 30px;

          color: #c99832;

          font-size: 28px;
        }


        .rh-global-callout p {

          margin: 0;

          color: #ffffff;

          font-size: 13px;

          line-height: 1.6;
        }


        /* ======================================================
           FOOTER TAGLINE
           ====================================================== */

        .rh-tagline {

          width: 100%;

          padding: 24px 20px;

          background: #071426;

          color: #ffffff;

          text-align: center;

          font-size:
            clamp(14px, 1.5vw, 22px);

          font-weight: 900;

          letter-spacing:
            clamp(1px, 0.3vw, 4px);
        }


        .rh-tagline span {

          color: #c99832;
        }


        /* ======================================================
           FOOTER
           ====================================================== */

        .rh-footer {

          width: 100%;

          padding:
            35px
            clamp(20px, 5vw, 90px);

          background: #f5f4ef;

          display: grid;

          grid-template-columns:
            auto
            1fr
            2fr;

          align-items: center;

          gap: 35px;

          border-top:
            1px solid
            #e3d6b7;
        }


        .rh-footer-badge {

          width: 95px;

          height: 95px;

          display: flex;

          align-items: center;

          justify-content: center;

          background: #c99832;

          color: #ffffff;

          font-family:
            Georgia,
            serif;

          font-size: 55px;

          font-weight: 900;

          clip-path:
            polygon(
              0 0,
              100% 0,
              80% 100%,
              0 100%
            );
        }


        .rh-footer-company {

          display: flex;

          flex-direction: column;
        }


        .rh-footer-company-name {

          color: #092746;

          font-size:
            clamp(18px, 1.7vw, 26px);

          font-weight: 900;

          letter-spacing: 0.5px;
        }


        .rh-footer-company-tagline {

          margin-top: 5px;

          color: #b08427;

          font-size: 14px;

          font-weight: 700;
        }


        .rh-footer-pillars {

          display: grid;

          grid-template-columns:
            repeat(5, 1fr);

          gap: 10px;
        }


        .rh-footer-pillar {

          min-height: 85px;

          padding: 10px;

          display: flex;

          flex-direction: column;

          align-items: center;

          justify-content: center;

          text-align: center;

          border-left:
            1px solid
            #d7ccb0;

          color: #34465a;

          font-size: 9px;

          line-height: 1.4;

          font-weight: 900;
        }


        .rh-footer-pillar svg {

          margin-bottom: 8px;

          color: #092746;

          font-size: 23px;
        }


        /* ======================================================
           REVEAL ANIMATION
           ====================================================== */

        .rh-reveal {

          opacity: 0;

          transform:
            translateY(45px);

          transition:
            opacity 0.8s ease,
            transform 0.8s ease;
        }


        .rh-reveal.rh-visible {

          opacity: 1;

          transform:
            translateY(0);
        }


        /* ======================================================
           TABLET
           ====================================================== */

        @media (max-width: 1100px) {

          .rh-hero {

            grid-template-columns: 1fr;
          }


          .rh-hero-left {

            padding:
              65px 7%;
          }


          .rh-hero-left::after {

            display: none;
          }


          .rh-hero-right {

            min-height: 540px;
          }


          .rh-logo {

            width: 330px;
          }


          .rh-info-grid {

            grid-template-columns: 1fr;
          }


          .rh-app-grid {

            grid-template-columns:
              repeat(2, 1fr);
          }


          /*
             STRENGTH CARD TABLET FIX
          */

          .rh-strength-grid {

            grid-template-columns: 1fr;

            gap: 25px;
          }


          .rh-strength-card {

            min-height: auto;

            padding:
              45px;

            padding-bottom: 0;
          }


          .rh-strength-card h2 {

            max-width: 700px;

            font-size:
              clamp(32px, 5vw, 48px);
          }


          .rh-strength-card p {

            max-width: 700px;

            padding-bottom: 235px;
          }


          .rh-strength-image {

            width: 60%;

            height: 220px;
          }


          .rh-advantages {

            min-height: auto;
          }


          .rh-footer {

            grid-template-columns:
              auto
              1fr;
          }


          .rh-footer-pillars {

            grid-column:
              1 / -1;
          }

        }


        /* ======================================================
           MEDIUM TABLET
           ====================================================== */

        @media (max-width: 850px) {

          .rh-strength-card {

            padding:
              35px 30px;

            padding-bottom: 0;
          }


          .rh-strength-card h2 {

            font-size: 38px;

            max-width: 600px;
          }


          .rh-strength-card p {

            max-width: 600px;

            padding-bottom: 210px;
          }


          .rh-strength-image {

            width: 65%;

            height: 200px;
          }

        }


        /* ======================================================
           MOBILE
           ====================================================== */

        @media (max-width: 700px) {

          .rh-top-nav {

            padding:
              12px 15px;
          }


          .rh-nav-brand {

            font-size: 12px;

            letter-spacing: 1px;
          }


          .rh-back-button {

            padding:
              8px 11px;

            font-size: 11px;
          }


          .rh-hero-left {

            padding:
              45px 20px 50px;
          }


          .rh-logo {

            width:
              min(300px, 100%);

            margin-bottom: 24px;
          }


          .rh-eyebrow {

            font-size: 9px;

            padding:
              7px 10px;
          }


          .rh-main-title {

            font-size:
              clamp(34px, 11vw, 48px);
          }


          .rh-hero-description {

            font-size: 13px;

            line-height: 1.65;
          }


          .rh-hero-description-secondary {

            font-size: 12px;
          }


          .rh-hero-right {

            min-height: 460px;
          }


          .rh-import-box {

            left: 15px;

            right: 15px;

            bottom: 15px;

            padding:
              17px 12px;
          }


          .rh-import-title {

            font-size: 16px;
          }


          .rh-import-grid {

            grid-template-columns:
              repeat(2, 1fr);
          }


          .rh-import-item {

            min-height: 70px;

            border-right: none;

            border-bottom:
              1px solid
              rgba(255,255,255,0.15);
          }


          .rh-import-item:nth-last-child(-n+2) {

            border-bottom: none;
          }


          .rh-import-icon {

            font-size: 21px;
          }


          .rh-import-item span {

            font-size: 8px;
          }


          .rh-section,
          .rh-app-section {

            padding:
              55px 15px;
          }


          .rh-info-grid {

            gap: 18px;
          }


          .rh-info-card {

            padding:
              20px 15px;
          }


          .rh-card-title {

            font-size: 15px;
          }


          .rh-product-text,
          .rh-why-text {

            font-size: 12px;
          }


          .rh-card-image {

            height: 190px;
          }


          .rh-app-grid {

            grid-template-columns: 1fr;

            gap: 15px;
          }


          .rh-app-card {

            height: 260px;
          }


          /* ==================================================
             MOBILE STRENGTH CARD
             ================================================== */

          .rh-strength-grid {

            grid-template-columns: 1fr;

            gap: 18px;
          }


          .rh-strength-card {

            min-height: 0;

            height: auto;

            padding:
              28px 20px 0;

            border-radius: 12px;

            display: flex;

            flex-direction: column;
          }


          .rh-strength-card h3 {

            font-size: 12px;

            letter-spacing: 1.8px;

            margin-bottom: 15px;
          }


          .rh-strength-card h2 {

            max-width: 100%;

            font-size:
              clamp(28px, 8.5vw, 38px);

            line-height: 1.08;

            margin: 0;
          }


          /*
             IMPORTANT:
             Remove the large desktop bottom
             padding on mobile.
          */

          .rh-strength-card p {

            max-width: 100%;

            margin:
              18px 0 25px;

            padding-bottom: 0;

            font-size: 13px;

            line-height: 1.7;
          }


          /*
             Image becomes normal-flow content
             on mobile. This completely prevents
             overlap with text.
          */

          .rh-strength-image {

            position: relative;

            width: calc(100% + 40px);

            height: 200px;

            right: auto;

            bottom: auto;

            margin-left: -20px;

            margin-top: 5px;

            border-radius:
              0
              0
              12px
              12px;

            flex-shrink: 0;
          }


          .rh-strength-image img {

            object-position:
              center center;
          }


          .rh-advantages {

            padding:
              22px 15px;

            min-height: auto;
          }


          .rh-advantages-grid {

            grid-template-columns:
              1fr 1fr;

            gap: 8px;
          }


          .rh-advantage {

            min-height: 125px;

            padding:
              12px 7px;
          }


          .rh-advantage-icon {

            width: 44px;

            height: 44px;

            font-size: 18px;
          }


          .rh-advantage span {

            font-size: 8px;
          }


          .rh-global-callout {

            align-items: flex-start;

            padding: 13px;

            gap: 10px;
          }


          .rh-global-callout p {

            font-size: 10px;
          }


          .rh-tagline {

            padding:
              20px 12px;

            font-size: 11px;

            line-height: 1.5;
          }


          .rh-footer {

            grid-template-columns: 1fr;

            padding:
              30px 20px;

            gap: 20px;

            text-align: center;
          }


          .rh-footer-badge {

            margin: auto;

            width: 75px;

            height: 75px;

            font-size: 40px;
          }


          .rh-footer-company {

            align-items: center;
          }


          .rh-footer-company-name {

            font-size: 16px;
          }


          .rh-footer-company-tagline {

            font-size: 11px;
          }


          .rh-footer-pillars {

            grid-template-columns:
              repeat(2, 1fr);

            gap: 0;
          }


          .rh-footer-pillar {

            min-height: 80px;

            font-size: 8px;
          }


          .rh-footer-pillar svg {

            font-size: 19px;
          }

        }


        /* ======================================================
           VERY SMALL MOBILE
           ====================================================== */

        @media (max-width: 400px) {

          .rh-nav-brand {

            display: none;
          }


          .rh-top-nav {

            justify-content:
              flex-start;
          }


          .rh-main-title {

            font-size: 32px;
          }


          .rh-hero-right {

            min-height: 430px;
          }


          .rh-import-box {

            padding:
              13px 8px;
          }


          .rh-import-title {

            font-size: 14px;
          }


          .rh-import-item span {

            font-size: 7px;
          }


          .rh-app-card {

            height: 230px;
          }


          .rh-advantages-grid {

            grid-template-columns: 1fr;
          }


          /*
             EXTRA SMALL STRENGTH FIX
          */

          .rh-strength-card {

            padding:
              25px 16px 0;
          }


          .rh-strength-card h2 {

            font-size: 29px;

            line-height: 1.1;
          }


          .rh-strength-card p {

            font-size: 12px;

            line-height: 1.65;

            margin-bottom: 22px;
          }


          .rh-strength-image {

            width: calc(100% + 32px);

            margin-left: -16px;

            height: 175px;
          }


          .rh-footer-pillars {

            grid-template-columns:
              1fr 1fr;
          }

        }


        /* ======================================================
           ACCESSIBILITY
           ====================================================== */

        @media (prefers-reduced-motion: reduce) {

          *,
          *::before,
          *::after {

            animation-duration:
              0.01ms !important;

            animation-iteration-count:
              1 !important;

            transition-duration:
              0.01ms !important;

            scroll-behavior:
              auto !important;
          }

        }

      `}</style>


      {/* ========================================================
          PAGE
          ======================================================== */}

      <div className="rh-page">


        {/* ======================================================
            TOP NAV
            ====================================================== */}

        <div className="rh-top-nav">

          <div className="rh-nav-brand">
            ASHRAF GROUP OF INDUSTRIES
          </div>


          <button
            className="rh-back-button"
            onClick={() => navigate('/')}
          >

            <FaArrowLeft />

            Back to All Sectors

          </button>

        </div>


        <main className="rh-main">


          {/* ====================================================
              HERO
              ==================================================== */}

          <section className="rh-hero">


            <div className="rh-hero-left">

              <img
                src={rhLogo}
                alt="RH Enterprises"
                className="rh-logo"
              />


              <div className="rh-eyebrow">

                SERVING SPACES,
                ELEVATING STANDARDS

              </div>


              <h1 className="rh-main-title">

                PREMIUM CARPET

                <span>
                  BACKING SOLUTIONS
                </span>

              </h1>


              <div className="rh-hero-description">

                RH Enterprises is a trusted importer
                and supplier of high-quality carpet
                backing materials, providing
                high-performance solutions that enhance
                durability, stability, and quality of
                carpets across industries.

              </div>


              <div className="rh-hero-description-secondary">

                We work closely with manufacturers,
                traders, and project developers to
                ensure consistent supply and reliable
                quality.

              </div>

            </div>


            <div className="rh-hero-right">

              <img
                src={cargoImage}
                alt="RH Enterprises Global Import"
                className="rh-cargo-image"
              />


              <div className="rh-import-box">

                <h2 className="rh-import-title">

                  WE IMPORT.

                  <span>
                    YOU CREATE EXCELLENCE.
                  </span>

                </h2>


                <div className="rh-import-grid">


                  <div className="rh-import-item">

                    <FaGlobe
                      className="rh-import-icon"
                    />

                    <span>

                      GLOBAL
                      <br />
                      SOURCING NETWORK

                    </span>

                  </div>


                  <div className="rh-import-item">

                    <FaCheckCircle
                      className="rh-import-icon"
                    />

                    <span>

                      CONSISTENT
                      <br />
                      QUALITY

                    </span>

                  </div>


                  <div className="rh-import-item">

                    <FaHandshake
                      className="rh-import-icon"
                    />

                    <span>

                      RELIABLE
                      <br />
                      SUPPLY

                    </span>

                  </div>


                  <div className="rh-import-item">

                    <FaDollarSign
                      className="rh-import-icon"
                    />

                    <span>

                      COMPETITIVE
                      <br />
                      PRICING

                    </span>

                  </div>


                </div>

              </div>

            </div>

          </section>


          {/* ====================================================
              PRODUCTS + WHY RH
              ==================================================== */}

          <section
            className="
              rh-section
              rh-section-light
            "
          >


            <div
              className="
                rh-section-header
                rh-reveal
              "
            >

              <div className="rh-section-kicker">

                RH ENTERPRISES

              </div>


              <h2 className="rh-section-title">

                Imported With Care.

                <br />

                Delivered With Confidence.

              </h2>


              <div className="rh-section-line" />

            </div>


            <div className="rh-info-grid">


              {/* ==================================================
                  PRODUCTS
                  ================================================== */}

              <div
                className="
                  rh-info-card
                  rh-reveal
                "
              >

                <h3 className="rh-card-title">

                  OUR PRODUCTS –
                  IMPORTED WITH CARE

                </h3>


                <div className="rh-product-list">


                  <div className="rh-product-item">

                    <div className="rh-check-box">
                      <FaCheck />
                    </div>

                    <span className="rh-product-text">

                      PP &amp; Jute Backing

                    </span>

                  </div>


                  <div className="rh-product-item">

                    <div className="rh-check-box">
                      <FaCheck />
                    </div>

                    <span className="rh-product-text">

                      Action Backing

                    </span>

                  </div>


                  <div className="rh-product-item">

                    <div className="rh-check-box">
                      <FaCheck />
                    </div>

                    <span className="rh-product-text">

                      Non-Woven Backing

                    </span>

                  </div>


                  <div className="rh-product-item">

                    <div className="rh-check-box">
                      <FaCheck />
                    </div>

                    <span className="rh-product-text">

                      Latex &amp; Secondary Backing

                    </span>

                  </div>


                  <div className="rh-product-item">

                    <div className="rh-check-box">
                      <FaCheck />
                    </div>

                    <span className="rh-product-text">

                      Custom Solutions as per Requirement

                    </span>

                  </div>


                </div>


                <div className="rh-card-image">

                  <img
                    src={backingImage}
                    alt="Carpet Backing Material"
                  />

                </div>

              </div>


              {/* ==================================================
                  WHY RH
                  ================================================== */}

              <div
                className="
                  rh-info-card
                  rh-reveal
                "
              >

                <h3 className="rh-card-title">

                  WHY RH ENTERPRISES?

                </h3>


                <div className="rh-why-list">


                  <div className="rh-why-item">

                    <FaGlobe
                      className="rh-why-icon"
                    />

                    <span className="rh-why-text">

                      Strong global sourcing network

                    </span>

                  </div>


                  <div className="rh-why-item">

                    <FaCheckCircle
                      className="rh-why-icon"
                    />

                    <span className="rh-why-text">

                      Consistent product quality

                    </span>

                  </div>


                  <div className="rh-why-item">

                    <FaTruck
                      className="rh-why-icon"
                    />

                    <span className="rh-why-text">

                      Reliable and timely supply

                    </span>

                  </div>


                  <div className="rh-why-item">

                    <FaDollarSign
                      className="rh-why-icon"
                    />

                    <span className="rh-why-text">

                      Competitive prices

                    </span>

                  </div>


                  <div className="rh-why-item">

                    <FaHandshake
                      className="rh-why-icon"
                    />

                    <span className="rh-why-text">

                      Customer-focused solutions

                    </span>

                  </div>


                </div>


                <div className="rh-card-image">

                  <img
                    src={warehouseImage}
                    alt="RH Enterprises Warehouse"
                  />

                </div>

              </div>

            </div>

          </section>


          {/* ====================================================
              APPLICATION AREAS
              ==================================================== */}

          <section className="rh-app-section">


            <div
              className="
                rh-section-header
                rh-reveal
              "
            >

              <div className="rh-section-kicker">

                WHERE WE SERVE

              </div>


              <h2 className="rh-section-title">

                APPLICATION AREAS

              </h2>


              <div className="rh-section-line" />

            </div>


            <div className="rh-app-grid">


              {/* RESIDENTIAL */}

              <div
                className="
                  rh-app-card
                  rh-reveal
                "
              >

                <img
                  src={residentialImage}
                  alt="Residential Carpets"
                />


                <div className="rh-app-overlay">

                  <div className="rh-app-icon">
                    <FaHome />
                  </div>

                  <span>
                    RESIDENTIAL
                    <br />
                    CARPETS
                  </span>

                </div>

              </div>


              {/* COMMERCIAL */}

              <div
                className="
                  rh-app-card
                  rh-reveal
                "
              >

                <img
                  src={commercialImage}
                  alt="Commercial Carpets"
                />


                <div className="rh-app-overlay">

                  <div className="rh-app-icon">
                    <FaBuilding />
                  </div>

                  <span>
                    COMMERCIAL
                    <br />
                    CARPETS
                  </span>

                </div>

              </div>


              {/* MOSQUE */}

              <div
                className="
                  rh-app-card
                  rh-reveal
                "
              >

                <img
                  src={mosqueImage}
                  alt="Prayer and Mosque Carpets"
                />


                <div className="rh-app-overlay">

                  <div className="rh-app-icon">
                    <FaPray />
                  </div>

                  <span>
                    PRAYER &amp; MOSQUE
                    <br />
                    CARPETS
                  </span>

                </div>

              </div>


              {/* AUTOMOTIVE */}

              <div
                className="
                  rh-app-card
                  rh-reveal
                "
              >

                <img
                  src={automotiveImage}
                  alt="Automotive Carpets"
                />


                <div className="rh-app-overlay">

                  <div className="rh-app-icon">
                    <FaCar />
                  </div>

                  <span>
                    AUTOMOTIVE
                    <br />
                    CARPETS
                  </span>

                </div>

              </div>


            </div>

          </section>


          {/* ====================================================
              STRENGTH + ADVANTAGES
              ==================================================== */}

          <section
            className="
              rh-section
              rh-section-dark
            "
          >


            <div className="rh-strength-grid">


              {/* ==================================================
                  OUR STRENGTH
                  ================================================== */}

              <div
                className="
                  rh-strength-card
                  rh-reveal
                "
              >

                <h3>
                  OUR STRENGTH
                </h3>


                <h2>

                  THE FOUNDATION BEHIND
                  EVERY QUALITY CARPET

                </h2>


                <p>

                  We don't just supply materials –
                  we provide the backbone that ensures
                  performance, durability, and
                  long-term value.

                </p>


                <div className="rh-strength-image">

                  <img
                    src={carpetRollsImage}
                    alt="Carpet Backing Rolls"
                  />

                </div>

              </div>


              {/* ==================================================
                  ADVANTAGES
                  ================================================== */}

              <div
                className="
                  rh-advantages
                  rh-reveal
                "
              >

                <h3>
                  KEY ADVANTAGES
                </h3>


                <div className="rh-advantages-grid">


                  <div className="rh-advantage">

                    <div
                      className="
                        rh-advantage-icon
                      "
                    >

                      <FaLink />

                    </div>


                    <span>

                      ENHANCED
                      <br />
                      STABILITY

                    </span>

                  </div>


                  <div className="rh-advantage">

                    <div
                      className="
                        rh-advantage-icon
                      "
                    >

                      <FaShieldAlt />

                    </div>


                    <span>

                      STRONGER
                      <br />
                      BONDING

                    </span>

                  </div>


                  <div className="rh-advantage">

                    <div
                      className="
                        rh-advantage-icon
                      "
                    >

                      <FaDumbbell />

                    </div>


                    <span>

                      HIGH
                      <br />
                      DURABILITY

                    </span>

                  </div>


                  <div className="rh-advantage">

                    <div
                      className="
                        rh-advantage-icon
                      "
                    >

                      <FaAward />

                    </div>


                    <span>

                      CONSISTENT
                      <br />
                      FINISHING QUALITY

                    </span>

                  </div>


                </div>


                <div className="rh-global-callout">

                  <FaGlobe />


                  <p>

                    We import from trusted global
                    manufacturers to ensure you get
                    the best quality backing materials
                    for superior carpet performance.

                  </p>

                </div>

              </div>

            </div>

          </section>


          {/* ====================================================
              TAGLINE
              ==================================================== */}

          <div className="rh-tagline">

            RELIABLE SOURCING.
            &nbsp;
            CONSISTENT QUALITY.
            &nbsp;

            <span>
              TRUSTED PARTNERSHIPS.
            </span>

          </div>


          {/* ====================================================
              FOOTER
              ==================================================== */}

          <footer className="rh-footer">


            <div className="rh-footer-badge">

              20

            </div>


            <div className="rh-footer-company">

              <span
                className="
                  rh-footer-company-name
                "
              >

                ASHRAF GROUP OF INDUSTRIES

              </span>


              <span
                className="
                  rh-footer-company-tagline
                "
              >

                Building Legacy.
                Powering the Future.

              </span>

            </div>


            <div className="rh-footer-pillars">


              <div className="rh-footer-pillar">

                <FaBriefcase />

                <span>

                  DIVERSE
                  <br />
                  PORTFOLIO

                </span>

              </div>


              <div className="rh-footer-pillar">

                <FaGlobe />

                <span>

                  GLOBAL
                  <br />
                  PRESENCE

                </span>

              </div>


              <div className="rh-footer-pillar">

                <FaShieldAlt />

                <span>

                  TRUSTED
                  <br />
                  EXCELLENCE

                </span>

              </div>


              <div className="rh-footer-pillar">

                <FaTruck />

                <span>

                  DELIVERING
                  <br />
                  VALUE

                </span>

              </div>


              <div className="rh-footer-pillar">

                <FaChartLine />

                <span>

                  GROWING
                  <br />
                  TOGETHER

                </span>

              </div>


            </div>

          </footer>

        </main>

      </div>

    </>
  );
}